/**
 * Active-event context switcher (FR-EVENTS-08).
 *
 * A persistent header control on every authenticated tenant page: reads the
 * session's event from `ActiveEventProvider`, and on selection updates the
 * global context — breadcrumbs, every event-scoped screen and (via
 * `APP_EVENTS.activeEventChanged`) the live-update subscription.
 *
 * The panel is a small, self-contained browser of `GET /events/v1`: season
 * filter, newest/oldest sort, name search and Previous/Next paging, so a
 * program with four seasons of practices can still find the right game.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../lib/api-error';
import type { TeamEvent } from '../lib/api-types';
import { APP_VIEWS } from '../lib/constants';
import {
  EVENT_STATUS_LABELS,
  EVENT_TYPE_LABELS,
  formatEventDateTime,
  formatEventLabel,
  seasonYearOptions,
} from '../lib/event-format';

import { useActiveEvent } from './active-event-context';
import { OPEN_EVENT_SWITCHER_EVENT } from './game-day-chrome';
import { useApiClient } from './providers';

const PANEL_LIMIT = 6;

export function ActiveEventSwitcher({ tenantSlug }: { tenantSlug: string }): JSX.Element {
  const apiClient = useApiClient();
  const { activeEvent, setActiveEvent } = useActiveEvent();
  const [isOpen, setIsOpen] = useState(false);
  const [season, setSeason] = useState('');
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [events, setEvents] = useState<readonly TeamEvent[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const restoreFocusRef = useRef(false);
  const close = useCallback((restoreFocus = true) => {
    restoreFocusRef.current = restoreFocus;
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement as HTMLElement | null;
      panelRef.current?.querySelector<HTMLElement>('select, input, button, a')?.focus();
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current = false;
      const opener = returnFocusRef.current;
      (opener?.isConnected ? opener : triggerRef.current)?.focus();
    }
  }, [isOpen]);

  // Play Entry no-event overlay (and other callers) can open this panel.
  useEffect(() => {
    const onOpen = (): void => {
      setPage(1);
      setIsOpen(true);
    };
    window.addEventListener(OPEN_EVENT_SWITCHER_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT_SWITCHER_EVENT, onOpen);
  }, []);

  // Fetch only while the panel is open — the header must not cost a request on
  // every page load.
  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    apiClient
      .listEvents(
        {
          page,
          limit: PANEL_LIMIT,
          sort_by: 'scheduled_at',
          sort_order: sortOrder,
          ...(season === '' ? {} : { season: Number(season) }),
          ...(search.trim() === '' ? {} : { q: search.trim() }),
        },
        controller.signal
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setEvents(result?.items ?? []);
        setTotalPages(Math.max(1, result?.pagination?.total_pages ?? 1));
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setEvents([]);
        setError(
          ApiError.isApiError(caught) ? caught.message : 'Events could not be loaded right now.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, isOpen, page, search, season, sortOrder]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (browserEvent: KeyboardEvent): void => {
      if (browserEvent.key === 'Escape') close();
    };
    const onPointerDown = (browserEvent: MouseEvent): void => {
      const node = containerRef.current;
      if (node !== null && !node.contains(browserEvent.target as Node)) close(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [close, isOpen]);

  function select(event: TeamEvent): void {
    setActiveEvent(event);
    close();
  }

  const buttonLabel = activeEvent === null ? 'No event selected' : formatEventLabel(activeEvent);

  return (
    <div className="event-switcher" ref={containerRef}>
      <button
        type="button"
        className="event-switcher__trigger"
        ref={triggerRef}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => {
          setPage(1);
          setIsOpen((current) => !current);
        }}
      >
        <span className="event-switcher__caption">Current Event</span>
        <span className="event-switcher__value">{buttonLabel}</span>
        <span aria-hidden="true" className="event-switcher__chevron">
          ⌄
        </span>
      </button>

      {isOpen ? (
        <div
          ref={panelRef}
          className="event-switcher__panel"
          role="dialog"
          aria-label="Switch active event"
        >
          <div className="event-switcher__filters">
            <label className="field">
              <span className="field__label">Season</span>
              <select
                value={season}
                onChange={(changeEvent) => {
                  setSeason(changeEvent.target.value);
                  setPage(1);
                }}
              >
                <option value="">All seasons</option>
                {seasonYearOptions().map((year) => (
                  <option key={year} value={String(year)}>
                    {year}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">Search</span>
              <input
                type="search"
                value={search}
                placeholder="Name or opponent"
                onChange={(changeEvent) => {
                  setSearch(changeEvent.target.value);
                  setPage(1);
                }}
              />
            </label>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setSortOrder((current) => (current === 'desc' ? 'asc' : 'desc'))}
            >
              {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            </button>
          </div>

          {error !== null ? (
            <p className="banner banner--error" role="alert">
              {error}
            </p>
          ) : null}
          {isLoading ? <p role="status">Loading events…</p> : null}
          {!isLoading && error === null && events.length === 0 ? (
            <p className="event-switcher__empty">
              No events match. <Link to={`/app/${tenantSlug}/${APP_VIEWS.events}`}>Create one</Link>{' '}
              in the Event Manager.
            </p>
          ) : null}

          <ul className="event-switcher__list">
            {events.map((event) => (
              <li key={event.event_id}>
                <button
                  type="button"
                  className={`event-switcher__option${
                    event.event_id === activeEvent?.event_id
                      ? ' event-switcher__option--current'
                      : ''
                  }`}
                  aria-current={event.event_id === activeEvent?.event_id}
                  onClick={() => select(event)}
                >
                  <span className="event-switcher__option-name">{event.name}</span>
                  <span className="event-switcher__option-meta">
                    {EVENT_TYPE_LABELS[event.event_type]} ·{' '}
                    {formatEventDateTime(event.scheduled_at)} · {EVENT_STATUS_LABELS[event.status]}
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <div className="event-switcher__footer">
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
          <Link
            className="event-switcher__manage"
            to={`/app/${tenantSlug}/${APP_VIEWS.events}`}
            onClick={() => close(false)}
          >
            Manage events →
          </Link>
        </div>
      ) : null}
    </div>
  );
}
